import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailureIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  TrendingUp as TrendingIcon,
  Block as BlockIcon,
  DeviceHub as ActivityIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface AuditLogItem {
  id: number;
  timestamp: string;
  action: string;
  actionDisplayName: string;
  actionColor: string;
  entityType: string;
  entityId: string;
  entityName: string;
  userId: number;
  username: string;
  userRole: string;
  description: string;
  ipAddress: string;
  status: string;
  statusColor: string;
  module: string;
  severity: string;
  severityColor: string;
  durationMs: number;
}

interface AuditLogStatistics {
  totalToday: number;
  successToday: number;
  failureToday: number;
  uniqueUsersToday: number;
  totalWeek: number;
  successWeek: number;
  failureWeek: number;
  successRate: number;
  actionDistribution: { action: string; displayName: string; count: number; color: string }[];
  moduleDistribution: { module: string; displayName: string; count: number; color: string }[];
  severityDistribution: { severity: string; displayName: string; count: number; color: string }[];
  topUsers: { userId: number; username: string; fullName: string; role: string; activityCount: number; lastActivity: string }[];
  securityEventsToday: number;
  failedLoginsToday: number;
  unauthorizedAccessToday: number;
}

interface SecurityEvent {
  id: number;
  timestamp: string;
  eventType: string;
  eventTypeColor: string;
  severity: string;
  severityColor: string;
  description: string;
  username: string;
  ipAddress: string;
  status: string;
  statusColor: string;
  module: string;
}

interface LoginHistoryItem {
  id: number;
  timestamp: string;
  username: string;
  fullName: string;
  role: string;
  action: string;
  actionColor: string;
  ipAddress: string;
  userAgent: string;
  status: string;
  statusColor: string;
  location: string;
}

interface SecurityDashboard {
  totalEventsToday: number;
  criticalEventsToday: number;
  failedLoginsToday: number;
  unauthorizedAccessToday: number;
  suspiciousActivityToday: number;
  eventTrend: { date: string; total: number; success: number; failure: number; securityEvents: number }[];
  recentEvents: SecurityEvent[];
  blockedIPs: { ipAddress: string; reason: string; blockedAt: string; failedAttempts: number; status: string }[];
  activeSessions: number;
}

const AuditLogsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [statistics, setStatistics] = useState<AuditLogStatistics | null>(null);
  const [securityDashboard, setSecurityDashboard] = useState<SecurityDashboard | null>(null);
  const [loginHistory, setLoginHistory] = useState<LoginHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        action: actionFilter,
        status: statusFilter,
        severity: severityFilter,
        module: moduleFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/audit-logs?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAuditLogs(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, actionFilter, statusFilter, severityFilter, moduleFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/audit-logs/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  const fetchSecurityDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/audit-logs/security-dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSecurityDashboard(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching security dashboard:', error);
    }
  }, []);

  const fetchLoginHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/audit-logs/login-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setLoginHistory(response.data.data.content);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
    }
  }, []);

  useEffect(() => {
    fetchAuditLogs();
    fetchStatistics();
    fetchSecurityDashboard();
    fetchLoginHistory();
  }, [fetchAuditLogs, fetchStatistics, fetchSecurityDashboard, fetchLoginHistory]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetail = (log: AuditLogItem) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getActionIcon = (action: string) => {
    if (action?.includes('LOGIN')) return <LoginIcon />;
    if (action?.includes('LOGOUT')) return <LogoutIcon />;
    if (action?.includes('CREATE')) return <SuccessIcon />;
    if (action?.includes('UPDATE')) return <TrendingIcon />;
    if (action?.includes('DELETE')) return <FailureIcon />;
    if (action?.includes('SECURITY')) return <SecurityIcon />;
    if (action?.includes('UNAUTHORIZED')) return <BlockIcon />;
    return <ActivityIcon />;
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return <ErrorIcon sx={{ color: '#991b1b' }} />;
      case 'ERROR': return <ErrorIcon sx={{ color: '#ef4444' }} />;
      case 'WARNING': return <WarningIcon sx={{ color: '#f59e0b' }} />;
      default: return <InfoIcon sx={{ color: '#3b82f6' }} />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const statCards = statistics ? [
    { title: "Today's Logs", value: statistics.totalToday, icon: <HistoryIcon />, color: '#2563eb' },
    { title: 'Success', value: statistics.successToday, icon: <SuccessIcon />, color: '#10b981' },
    { title: 'Failures', value: statistics.failureToday, icon: <FailureIcon />, color: '#ef4444' },
    { title: 'Unique Users', value: statistics.uniqueUsersToday, icon: <PersonIcon />, color: '#8b5cf6' },
    { title: 'Security Events', value: statistics.securityEventsToday, icon: <SecurityIcon />, color: '#f59e0b' },
    { title: 'Success Rate', value: `${statistics.successRate.toFixed(1)}%`, icon: <TrendingIcon />, color: '#06b6d4' },
  ] : [];

  const securityCards = securityDashboard ? [
    { title: 'Security Events', value: securityDashboard.totalEventsToday, icon: <SecurityIcon />, color: '#ef4444' },
    { title: 'Critical', value: securityDashboard.criticalEventsToday, icon: <ErrorIcon />, color: '#991b1b' },
    { title: 'Failed Logins', value: securityDashboard.failedLoginsToday, icon: <BlockIcon />, color: '#f59e0b' },
    { title: 'Unauthorized', value: securityDashboard.unauthorizedAccessToday, icon: <WarningIcon />, color: '#f97316' },
    { title: 'Suspicious', value: securityDashboard.suspiciousActivityToday, icon: <WarningIcon />, color: '#eab308' },
    { title: 'Active Sessions', value: securityDashboard.activeSessions, icon: <PersonIcon />, color: '#10b981' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Audit Logs & Security Monitoring
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Monitor system activities, security events, and user actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchAuditLogs(); fetchStatistics(); fetchSecurityDashboard(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label="Audit Logs" icon={<HistoryIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Security Dashboard" icon={<SecurityIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Login History" icon={<LoginIcon />} sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Tab 1: Audit Logs */}
        {activeTab === 0 && (
          <Box>
            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ p: 2 }}>
              {statCards.map((stat, index) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                  <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                            {stat.title}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${stat.color}15`, color: stat.color }}>
                          {stat.icon}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Filters */}
            <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
              <TextField
                size="small"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#64748b' }} /></InputAdornment>,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Action</InputLabel>
                <Select value={actionFilter} label="Action" onChange={(e) => setActionFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="LOGIN">Login</MenuItem>
                  <MenuItem value="LOGOUT">Logout</MenuItem>
                  <MenuItem value="CREATE">Create</MenuItem>
                  <MenuItem value="UPDATE">Update</MenuItem>
                  <MenuItem value="DELETE">Delete</MenuItem>
                  <MenuItem value="VIEW">View</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="SUCCESS">Success</MenuItem>
                  <MenuItem value="FAILURE">Failure</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Severity</InputLabel>
                <Select value={severityFilter} label="Severity" onChange={(e) => setSeverityFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="INFO">Info</MenuItem>
                  <MenuItem value="WARNING">Warning</MenuItem>
                  <MenuItem value="ERROR">Error</MenuItem>
                  <MenuItem value="CRITICAL">Critical</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Module</InputLabel>
                <Select value={moduleFilter} label="Module" onChange={(e) => setModuleFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="AUTH">Authentication</MenuItem>
                  <MenuItem value="APPOINTMENT">Appointments</MenuItem>
                  <MenuItem value="DOCTOR">Doctors</MenuItem>
                  <MenuItem value="PATIENT">Patients</MenuItem>
                  <MenuItem value="PRESCRIPTION">Prescriptions</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'transparent' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Module</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Severity</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8}><LinearProgress /></TableCell></TableRow>
                  ) : auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <Typography sx={{ color: '#64748b' }}>No audit logs found</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getActionIcon(log.action)}
                            label={log.actionDisplayName || log.action}
                            size="small"
                            sx={{ bgcolor: `${log.actionColor}15`, color: log.actionColor, fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'transparent' }}>
                              {log.username?.charAt(0)?.toUpperCase() || 'S'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{log.username}</Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>{log.userRole}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{log.module}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={log.status === 'SUCCESS' ? <SuccessIcon /> : <FailureIcon />}
                            label={log.status}
                            size="small"
                            sx={{ bgcolor: `${log.statusColor}15`, color: log.statusColor, fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getSeverityIcon(log.severity)}
                            <Typography variant="body2">{log.severity}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleViewDetail(log)}>
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        )}

        {/* Tab 2: Security Dashboard */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            {/* Security Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {securityCards.map((stat, index) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                  <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>{stat.title}</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>{stat.value}</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${stat.color}15`, color: stat.color }}>{stat.icon}</Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Recent Security Events */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Recent Security Events</Typography>
                <List>
                  {securityDashboard?.recentEvents?.map((event, index) => (
                    <ListItem key={event.id || index} divider={index < (securityDashboard?.recentEvents?.length || 0) - 1}>
                      <ListItemIcon>
                        <Box sx={{ p: 1, borderRadius: 1, bgcolor: `${event.eventTypeColor}15` }}>
                          {getSeverityIcon(event.severity)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={event.description}
                        secondary={`${event.username} • ${event.ipAddress} • ${formatTimestamp(event.timestamp)}`}
                      />
                      <Chip label={event.severity} size="small" sx={{ bgcolor: `${event.severityColor}15`, color: event.severityColor }} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Blocked IPs */}
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Blocked IP Addresses</Typography>
                {securityDashboard?.blockedIPs?.length === 0 ? (
                  <Typography sx={{ color: '#64748b' }}>No blocked IPs</Typography>
                ) : (
                  <List>
                    {securityDashboard?.blockedIPs?.map((ip, index) => (
                      <ListItem key={index} divider>
                        <ListItemIcon><BlockIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
                        <ListItemText primary={ip.ipAddress} secondary={`${ip.reason} • ${ip.failedAttempts} failed attempts`} />
                        <Chip label={ip.status} size="small" sx={{ bgcolor: 'transparent' , color: '#fff' }} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab 3: Login History */}
        {activeTab === 2 && (
          <Box sx={{ p: 2 }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Recent Login Activity</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'transparent' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>IP Address</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loginHistory.map((item, index) => (
                        <TableRow key={item.id || index} hover>
                          <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{formatTimestamp(item.timestamp)}</Typography></TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.fullName}</Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>{item.role}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={item.action} size="small" sx={{ bgcolor: `${item.actionColor}15`, color: item.actionColor, fontWeight: 500, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell><Typography variant="body2">{item.ipAddress}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{item.location}</Typography></TableCell>
                          <TableCell>
                            <Chip label={item.status} size="small" sx={{ bgcolor: `${item.statusColor}15`, color: item.statusColor, fontWeight: 500, fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Audit Log Details</Typography>
          <IconButton onClick={() => setDetailDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Timestamp</Typography>
                <Typography variant="body1">{formatTimestamp(selectedLog.timestamp)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Action</Typography>
                <Chip label={selectedLog.actionDisplayName || selectedLog.action} size="small" sx={{ bgcolor: `${selectedLog.actionColor}15`, color: selectedLog.actionColor }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>User</Typography>
                <Typography variant="body1">{selectedLog.username} ({selectedLog.userRole})</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Module</Typography>
                <Typography variant="body1">{selectedLog.module}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Description</Typography>
                <Typography variant="body1">{selectedLog.description}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Status</Typography>
                <Chip label={selectedLog.status} size="small" sx={{ bgcolor: `${selectedLog.statusColor}15`, color: selectedLog.statusColor }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Severity</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getSeverityIcon(selectedLog.severity)}
                  <Typography variant="body1">{selectedLog.severity}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Entity Type</Typography>
                <Typography variant="body1">{selectedLog.entityType}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Entity ID</Typography>
                <Typography variant="body1">{selectedLog.entityId || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>IP Address</Typography>
                <Typography variant="body1">{selectedLog.ipAddress}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Duration</Typography>
                <Typography variant="body1">{selectedLog.durationMs ? `${selectedLog.durationMs}ms` : '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;